import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { Mail, Send, Clock, Loader2 } from "lucide-react";

const Contact = () => {
  const { t } = useApp();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast({ description: t.contact.success });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast({ description: t.contact.error });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-[#0a0c10] border-white/10 text-gray-200 h-11 focus-visible:ring-blue-500";

  return (
    <div className="pt-16 min-h-screen">
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-3xl mx-auto px-5 py-20 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.contact.title}</h1>
          <p className="text-gray-400 mt-4 text-lg">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-16 grid md:grid-cols-5 gap-8">
        {/* Info */}
        <div className="md:col-span-2 space-y-5">
          <div className="bg-[#0f1116] border border-white/8 rounded-2xl p-6">
            <h3 className="font-semibold text-white mb-4">{t.contact.infoTitle}</h3>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-xs text-gray-500">{t.contact.emailLabel}</div>
                <a href="mailto:info@scoutos.eu" className="text-gray-200 text-sm hover:text-blue-400 transition-colors">info@scoutos.eu</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="text-sm text-gray-300">{t.contact.responseTime}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-3">
          <form onSubmit={handleSubmit} className="bg-[#0f1116] border border-white/8 rounded-2xl p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-400 mb-1.5 block">{t.contact.name}</Label>
                <Input value={form.name} onChange={update("name")} placeholder={t.contact.namePh} className={inputCls} required />
              </div>
              <div>
                <Label className="text-sm text-gray-400 mb-1.5 block">{t.contact.email}</Label>
                <Input type="email" value={form.email} onChange={update("email")} placeholder={t.contact.emailPh} className={inputCls} required />
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-400 mb-1.5 block">{t.contact.subject}</Label>
              <Input value={form.subject} onChange={update("subject")} placeholder={t.contact.subjectPh} className={inputCls} />
            </div>
            <div>
              <Label className="text-sm text-gray-400 mb-1.5 block">{t.contact.message}</Label>
              <Textarea value={form.message} onChange={update("message")} placeholder={t.contact.messagePh} className="bg-[#0a0c10] border-white/10 text-gray-200 min-h-[140px] focus-visible:ring-blue-500" required />
            </div>
            <Button type="submit" disabled={sending} className="bg-blue-600 hover:bg-blue-500 text-white h-11 rounded-xl font-medium w-full sm:w-auto px-8">
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.contact.sending}</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> {t.contact.send}</>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
